import React from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Link } from "react-router-dom";
import { formatDate } from "../../utils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const Courses = {
  1: "Maths",
  2: "Science",
  3: "Physics",
  4: "Chemistry",
  5: "English",
};

const ParseSubject = (value) => {
  return Courses[value];
};

const CustomHeader = ({ date, onDownload }) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    <span>{formatDate(new Date(date))}</span>
    <i
      className="bi bi-file-earmark-arrow-down-fill"
      onClick={onDownload}
      style={{ marginLeft: "5px", cursor: "pointer" }}
      title="Download"
    />
  </div>
);

const StudentMarksTable = ({ data }) => {
  const isAdmin = sessionStorage.getItem("role") === "Admin";
  const sortedData = JSON.parse(JSON.stringify(data));

  sortedData.students.forEach((student) => {
    student.marksDetails.sort(
      (a, b) => new Date(b.dateofExam) - new Date(a.dateofExam)
    );
  });

  const dates = sortedData.students[0].marksDetails.map(
    (item) => item.dateofExam
  );

  const subject = ParseSubject(data.courses);

  const columnDefs = [
    {
      headerName: "Student Name",
      field: "studentName",
      cellRenderer: (params) => (
        <Link to={`/studentDashboard/${params.data.studentId}`}>
          {params.data.studentName}
        </Link>
      ),
    },
    { headerName: "Class", field: "class", filter: false, sortable: false },
    { headerName: "Subject", field: "subject", filter: false, sortable: false },
    ...dates.map((date, index) => ({
      headerComponent: (props) => (
        <CustomHeader date={date} onDownload={() => downloadPDF(date)} />
      ),
      field: `marksDetail_${index}`,
      cellRenderer: (params) => {
        const marks = params.value;
        const maxMarks = params.data[`marksDetailMax_${index}`];
        const studentName = params.data.studentName;
        const parentContact = params.data.parentContact;
        // Format as Indian WhatsApp number (country code 91 + 10-digit mobile)
        const phone = parentContact ? `91${String(parentContact).replace(/\D/g, "")}` : "";

        const absent = marks == null || marks === -1;
        const message =
          `Hello, Greetings from Ooklass Tuition Classes\n` +
          `Student - ${studentName}\n` +
          `Marks - ${!absent ? `${marks}/${maxMarks}` : "Absent"}\n` +
          `Subject - ${subject}\n` +
          `Date of test - ${formatDate(new Date(date))}\n\n`;

        const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span>{absent ? "Absent" : marks}</span>
            {isAdmin && (
              <a href={whatsappUrl} target="_blank" rel="noreferrer">
                <WhatsAppIcon
                  style={{ color: "#25D366", fontSize: "18px", cursor: "pointer" }}
                />
              </a>
            )}
          </div>
        );
      },
    })),
  ];

  const rowData = sortedData.students.map((student) => {
    const studentData = {
      studentId: student.studentId,
      studentName: student.studentName,
      class: data.class,
      subject: subject,
      parentContact: student.parentContact ?? null,
    };
    student.marksDetails.forEach((marksDetail, index) => {
      studentData[`marksDetail_${index}`] = marksDetail.marks;
      studentData[`marksDetailMax_${index}`] = marksDetail.maxMarks;
    });
    return studentData;
  });

  const downloadPDF = (date) => {
    const pdf = new jsPDF();

    const formattedDate = formatDate(new Date(date)).replace(/\//g, "-");
    const pdfName = `class${data.class}_${subject}_${formattedDate}.pdf`;

    const pdfData = sortedData.students.map((student) => {
      const marks = student.marksDetails.find(
        (m) => m.dateofExam === date
      )?.marks;
      let maxMarks = student.marksDetails.find(
        (m) => m.dateofExam === date
      )?.maxMarks;
      return { name: student.studentName, marks, maxMarks };
    });

    pdfData.sort((a, b) => b?.marks - a?.marks);
    pdfData.forEach((item, index) => {
      item.rank = index + 1;
    });

    pdf.text(
      `Results for Class - ${data.class} | Subject - ${subject} | Date - ${formattedDate}`,
      14,
      10
    );

    autoTable(pdf, {
      head: [["Rank", "Name", "Marks"]],
      body: pdfData.map((item) => [
        item.rank,
        item.name,
        item.marks === -1 || item.marks == null ? "Absent" : `${item.marks}/${item.maxMarks}`,
      ]),
      startY: 20,
    });

    pdf.save(pdfName);
  };

  const onGridReady = (params) => {
    params.api.sizeColumnsToFit();
  };

  const getRowHeight = () => 30;

  return (
    <div className="ag-theme-alpine" style={{ height: 440, width: "100%" }}>
      <AgGridReact
        columnDefs={columnDefs}
        rowData={rowData}
        pagination={true}
        paginationPageSize={20}
        defaultColDef={{
          resizable: true,
          sortable: true,
          filter: true,
        }}
        animateRows={true}
        onGridReady={onGridReady}
        getRowHeight={getRowHeight}
      />
    </div>
  );
};

export default StudentMarksTable;
